import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Headers/Header";
import InteractiveTour from "../components/JoyrideTours/InteractiveTour";
import { NewReleasesTickerSection } from "../components/Modals/NewReleasesModal";
import NewReleasesModal from "../components/Modals/NewReleasesModal";
import { CraftButton } from "@craft-design-system/core-components-react";
import {
	shouldShowTour,
	markTourCompleted,
	PAGE_NAMES,
} from "../utils/firstTimeVisit";
import { signOut } from "../utils/authorizationUtils";
import { useLanguage } from "../hooks/useLanguage";

// Hero slideshow images (tif files excluded — not supported by browsers)
const heroSlides = [
	{ src: "/8.jpg", alt: "Manufacturing Process" },
	{ src: "/6.png", alt: "Vehicle Tracking" },
	{ src: "/12.jpg", alt: "Supply Chain Management" },
];

// Development Feature Flags
const ENABLE_VFF_PAGE = true;
const ENABLE_SERIES_PAGE = true;

const developmentStages = [
	{
		id: "supply-type",
		title: "Supply Type Allocation",
		subtitle: "SE-Team Procurement, Allocation & Planning",
		icon: "🔧",
		stage: 1,
		description:
			"Initial procurement, parts categorization and requisition planning",
		path: "/supply-type-allocation",
		enabled: true,
		cardImage: "/supplytype.png",
	},
	{
		id: "vff",
		title: "Vehicle Tracking",
		subtitle: "Vorserien‑Freigabe‑Fahrzeug",
		icon: "🚛",
		stage: 2,
		description:
			"Track vehicle functions, supplier nomination and features development",
		path: "/vff",
		enabled: ENABLE_VFF_PAGE,
		cardImage: "/vff.png",
	},        {
                id: "series",
                title: "Series Tracking",
                subtitle: "Production Series Monitoring",
                icon: "📦",
                stage: 3,
                description:
                        "Monitor production series and manufacturing progress of parts",
                path: "/series-tracking",
        enabled: ENABLE_SERIES_PAGE,
                cardImage: "/series.png",
        },
];

const Mainscreen = ({ props }) => {
	const { username } = props;
	useEffect(() => {
		document.title = "MAN PTT - HomePage";
	}, []);
	const [isTourOpen, setIsTourOpen] = useState(false);
	const [isReleasesModalOpen, setIsReleasesModalOpen] = useState(false);
	const [slideIndex, setSlideIndex] = useState(0);
	const slideTimerRef = useRef(null);
	const navigate = useNavigate();
	const { t } = useLanguage();

	// Auto-advance slideshow every 5 seconds
	const startSlideTimer = useCallback(() => {
		slideTimerRef.current = setInterval(() => {
			setSlideIndex((prev) => (prev + 1) % heroSlides.length);
		}, 5000);
	}, []);

	useEffect(() => {
		startSlideTimer();
		return () => clearInterval(slideTimerRef.current);
	}, [startSlideTimer]);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const view = urlParams.get("view");
		if (view) {
			let targetPath = "";
			switch (view.toLowerCase()) {
				case "vff":
					targetPath = "/vff";
					break;
				case "series":
					targetPath = "/series-tracking";
					break;
				case "dashboard":
					targetPath = "/dashboard";
					break;
				case "supply-type":
					targetPath = "/supply-type-allocation";
					break;
				default:
					return;
			}
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.delete("view");
			const queryString = searchParams.toString();
			const fullPath = queryString
				? `${targetPath}?${queryString}`
				: targetPath;
			navigate(fullPath, { replace: true });
		}
	}, [navigate]);
	const getDisplayUsername = (fullUsername) => {
		if (fullUsername && fullUsername.includes("@")) {
			return fullUsername.split("@")[0];
		}
		return fullUsername || "User";
	};

	const displayUsername = getDisplayUsername(username);

	// First name only, capitalised — e.g. "yash.sakhare" → "Yash"
	const displayFirstName = (() => {
		const base = displayUsername.split(".")[0];
		return base.charAt(0).toUpperCase() + base.slice(1);
	})();

	const handleNavigation = (stage) => {
		if (!stage.enabled) return;
		window.location.href = stage.path;
	};

	const handleStartTour = () => setIsTourOpen(true);
	const handleCloseTour = () => {
		setIsTourOpen(false);
		markTourCompleted(PAGE_NAMES.MAINSCREEN);
	};

	useEffect(() => {
		if (shouldShowTour(PAGE_NAMES.MAINSCREEN)) {
			const timer = setTimeout(() => setIsTourOpen(true), 1000);
			return () => clearTimeout(timer);
		}
	}, []);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100vh",
				backgroundColor: "#ffffff",
			}}
		>
			<Header
				signOut={signOut}
				user={{ profile: { preferred_username: displayUsername } }}
				onStartTour={handleStartTour}
			/>
			<InteractiveTour isOpen={isTourOpen} onClose={handleCloseTour} />{" "}
			{/* Main content with top padding for fixed header */}
			<div
				style={{
					paddingTop: "calc(64px + var(--banner-height, 0px))",
					flex: 1,
					paddingLeft: "20px",
					paddingRight: "20px",
				}}
			>
				{/* ── Hero Banner ── */}
				<div
					style={{
						margin: "24px 32px",
						background:
							"linear-gradient(135deg, #fce4ec 0%, #fdf0f3 60%, #fff5f7 100%)",
						borderRadius: "16px",
						padding: "40px 48px",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						overflow: "hidden",
						position: "relative",
						minHeight: "200px",
					}}
				>
					<div style={{ zIndex: 2, maxWidth: "50%" }}>
						<h1
							style={{
								fontSize: "2rem",
								fontWeight: "800",
								color: "white",
								margin: "0 0 12px 0",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
								lineHeight: 1.2,
							}}
						>
							{t("landing.heroGreeting", { name: displayFirstName })}
						</h1>{" "}
						<p
							style={{
								fontSize: "0.95rem",
								color: "white",
								margin: "0 0 28px 0",
								lineHeight: 1.6,
								maxWidth: "650px",
							}}
						>
							{t("landing.heroSubtitle")}{" "}
						</p>{" "}
						<button
							data-stage="dashboard"
							onClick={() => navigate("/dashboard?view=vff")}
							style={{
								backgroundColor: "#cc0000",
								color: "white",
								border: "none",
								padding: "11px 24px",
								fontSize: "0.92rem",
								fontWeight: "700",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
								cursor: "pointer",
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								transition: "background 0.15s, transform 0.15s",
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.backgroundColor = "#a80000";
								e.currentTarget.style.transform = "translateY(-1px)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.backgroundColor = "#cc0000";
								e.currentTarget.style.transform = "translateY(0)";
							}}
						>
							{t("landing.goToDashboard")}{" "}
							<span style={{ fontSize: "1rem" }}></span>
						</button>
					</div>{" "}
					{/* Hero slideshow */}
					<div
						style={{
							position: "absolute",
							right: 0,
							top: 0,
							bottom: 0,
							width: "100%",
							overflow: "hidden",
							borderRadius: "0 16px 16px 0",
						}}
					>
						{heroSlides.map((slide, i) => (
							<img
								key={slide.src}
								src={slide.src}
								alt={slide.alt}
								style={{
									position: "absolute",
									inset: 0,
									width: "100%",
									height: "100%",
									objectFit: "cover",
									objectPosition: "center",
									opacity: i === slideIndex ? 1 : 0,
									transition: "opacity 0.8s ease-in-out",
								}}
								onError={(e) => {
									e.currentTarget.style.display = "none";
								}}
							/>
						))}
						{/* Dark overlay so text above remains legible */}
						<div
							style={{
								position: "absolute",
								inset: 0,
								background:
									"linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.15) 100%)",
								zIndex: 1,
								borderRadius: "0 16px 16px 0",
							}}
						/>
						{/* Dot indicators */}
						<div
							style={{
								position: "absolute",
								bottom: "10px",
								left: "50%",
								transform: "translateX(-50%)",
								display: "flex",
								gap: "6px",
								zIndex: 3,
							}}
						>
							{heroSlides.map((_, i) => (
								<button
									key={i}
									onClick={() => {
										setSlideIndex(i);
										clearInterval(slideTimerRef.current);
										startSlideTimer();
									}}
									style={{
										width: i === slideIndex ? "20px" : "8px",
										height: "8px",
										borderRadius: "4px",
										border: "none",
										backgroundColor:
											i === slideIndex ? "#cc0000" : "rgba(255,255,255,0.7)",
										cursor: "pointer",
										padding: 0,
										transition: "width 0.3s ease, background-color 0.3s ease",
									}}
									aria-label={`Go to slide ${i + 1}`}
								/>
							))}
						</div>
					</div>
				</div>
				{/* ── Parts Tracking Lifecycle ── */}
				<div style={{ margin: "0 32px 32px" }}>
					<h2
						style={{
							fontSize: "1.1rem",
							fontWeight: "700",
							color: "#1e2832",
							margin: "0 0 16px 0",
							fontFamily: "MAN EUROPE_Bold, sans-serif",
						}}
					>
						{t("landing.lifecycleTitle")}
					</h2>{" "}
					<div
						className="workflow-diagram"
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: "16px",
						}}
					>
						{developmentStages.map((stage) => (
							<div
								key={stage.id}
								data-stage={stage.id}
								style={{
									backgroundColor: "white",
									borderRadius: "12px",
									display: "flex",
									alignItems: "stretch",
									boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
									border: "1px solid #f0f0f0",
									transition: "all 0.25s ease",
									cursor: stage.enabled ? "default" : "not-allowed",
									opacity: stage.enabled ? 1 : 0.6,
									height: "130px",
									overflow: "hidden",
								}}
								onMouseEnter={(e) => {
									if (stage.enabled) {
										e.currentTarget.style.boxShadow =
											"0 8px 24px rgba(0,0,0,0.12)";
										e.currentTarget.style.transform = "translateY(-2px)";
									}
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.boxShadow =
										"0 2px 8px rgba(0,0,0,0.06)";
									e.currentTarget.style.transform = "translateY(0)";
								}}
							>
								{" "}
								{/* Left 60%: text info */}
								<div
									style={{
										flex: "0 0 70%",
										padding: "12px 16px",
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
									}}
								>
									<h3
										style={{
											fontSize: "0.88rem",
											fontWeight: "700",
											color: "#1e2832",
											margin: "0 0 4px 0",
											fontFamily: "MAN EUROPE_Bold, sans-serif",
										}}
									>
										{t(`landing.stages.${stage.id}.title`)}
									</h3>
									<p
										style={{
											fontSize: "0.75rem",
											color: "#888",
											margin: "0 0 10px 0",
											lineHeight: 1.3,
										}}
									>
										{t(`landing.stages.${stage.id}.description`)}
									</p>{" "}
									{stage.enabled ? (
										<a
											href={stage.path}
											onClick={(e) => {
												if (
													!e.ctrlKey &&
													!e.metaKey &&
													!e.shiftKey &&
													e.button !== 1
												) {
													e.preventDefault();
													handleNavigation(stage);
												}
											}}
											style={{
												textDecoration: "none",
												display: "inline-block",
											}}
										>
											<CraftButton
												purpose="standard"
												variant="primary"
												size="m"
												type="button"
												icon-name="chevron-right"
											>
												{t("landing.continue")}
											</CraftButton>
										</a>
									) : (
										<span
											style={{
												display: "inline-block",
												backgroundColor: "#f0f0f0",
												color: "#999",
												borderRadius: "20px",
												padding: "6px 18px",
												fontSize: "0.82rem",
												fontWeight: "600",
												fontFamily: "MAN EUROPE_Bold, sans-serif",
											}}
										>
											{t("landing.comingSoon")}
										</span>
									)}
								</div>{" "}
								{/* Right 40%: light yellow panel with image */}
								<div
									style={{
										flex: "0 0 25%",
										backgroundColor: "#dbeafe",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										overflow: "hidden",
									}}
								>
									<img
										src={stage.cardImage}
										alt={stage.title}
										style={{
											maxWidth: "80%",
											maxHeight: "70px",
											width: "auto",
											height: "auto",
											objectFit: "contain",
										}}
										onError={(e) => {
											e.currentTarget.style.display = "none";
											e.currentTarget.parentElement.innerHTML = `<span style="font-size:1.8rem;display:flex;align-items:center;justify-content:center;width:100%;height:100%">${stage.icon}</span>`;
										}}
									/>
								</div>
							</div>
						))}
					</div>
				</div>{" "}
				{/* ── New Releases Ticker Section ── */}
				<NewReleasesTickerSection
					onOpenModal={() => setIsReleasesModalOpen(true)}
				/>
			</div>
			{/* New Releases Modal */}
			<NewReleasesModal
				open={isReleasesModalOpen}
				onClose={() => setIsReleasesModalOpen(false)}
			/>
			{/* Footer */}
			<footer
				style={{
					width: "100%",
					padding: "16px 24px",
					textAlign: "center",
					backgroundColor: "#f8f8f8",
					borderTop: "1px solid #ddd",
					fontSize: "0.82rem",
					color: "#777",
					boxSizing: "border-box",
				}}
			>
				{t("landing.footerRights", { year: new Date().getFullYear() })}
				<br />
				{t("landing.footerConfidential")}
			</footer>
		</div>
	);
};

export default Mainscreen;
